import baseapi from './baseapi';
import rulebaseapi from './ruleengine';

export interface CatalogData {
  sources: Array<{
    name: string;
    label: string;
    description: string;
    icon: string;
    signal_types: Array<{ value: string; label: string }>;
    options: Array<{
      name: string;
      label: string;
      type: 'number' | 'multi_select' | 'tags' | 'text';
      values?: string[];
      default?: any;
    }>;
    fields: Array<{
      path: string;
      label: string;
      type: 'text' | 'number' | 'boolean' | 'ip' | 'list' | 'datetime';
      values?: string[];
      example?: string;
    }>;
  }>;
  operators: Array<{ value: string; label: string; needs_value: boolean }>;
  operators_by_type: Record<string, string[]>;
  action_types: Array<{
    type: string;
    label: string;
    icon: string;
    description: string;
    integration_types?: string[];
    config_fields: Array<{
      name: string;
      label: string;
      type: 'text' | 'number' | 'boolean' | 'template_text' | 'template_html' | 'json' | 'select' | 'channels' | 'integration' | 'script' | 'oncall_schedule';
      values?: Array<{ value: any; label: string }>;
      required?: boolean;
    }>;
  }>;
  severities: string[];
  template_variables: string[];
}

export interface LookupsData {
  clients: Array<{ id: number | string; name: string }>;
  tags: Array<{ id: number | string; name: string }>;
  integrations: Array<{ id: number | string; name: string; integration_type: string; client_id: number | string }>;
  device_types: string[];
  scripts: Array<{ id: number | string; name: string; description: string; script_type: string }>;
  oncall_schedules: Array<{ id: number | string; name: string }>;
}

export const getClientId = (): number | undefined => {
  try {
    const stored = localStorage.getItem("level");
    if (stored) {
      const level = JSON.parse(stored);
      if (level && level.name !== "global" && (level.id !== undefined || level.client_id !== undefined)) {
        const idVal = level.id !== undefined ? level.id : level.client_id;
        const num = Number(idVal);
        return !isNaN(num) ? num : undefined;
      }
    }
  } catch { }
  return undefined;
};

export const withClientIdParams = (params: any = {}) => {
  const clientId = params?.client_id !== undefined ? params.client_id : getClientId();

  let queryObj: any = {};
  if (params && params.query) {
    try {
      queryObj = typeof params.query === 'string' ? JSON.parse(params.query) : { ...params.query };
    } catch {
      queryObj = {};
    }
  }

  const { query, include_global, ...otherParams } = params || {};

  if (include_global) {
    const resultParams: any = { ...otherParams, include_global: true };
    if (Object.keys(queryObj).length > 0) {
      resultParams.query = JSON.stringify(queryObj);
    }
    return resultParams;
  }

  if (clientId !== undefined) {
    queryObj.client_id = clientId;
    return {
      ...otherParams,
      client_id: clientId,
      query: JSON.stringify(queryObj),
    };
  }

  delete queryObj.client_id;
  delete otherParams.client_id;

  const resultParams: any = { ...otherParams };
  if (Object.keys(queryObj).length > 0) {
    resultParams.query = JSON.stringify(queryObj);
  }
  return resultParams;
};

export const withClientIdBody = (data: any = {}) => {
  const clientId = getClientId();
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    if (clientId !== undefined) {
      return { client_id: clientId, ...data };
    }
    const { client_id, ...rest } = data;
    return rest;
  }
  return data;
};

export const automationApi = {
  getRuleEngineCollections: (assetType: string = 'all', params?: any) =>
    baseapi.get<any>(`/ruleengine/collections/${assetType}`, { params: withClientIdParams(params) }),

  getRuleEngineOperators: (params?: any) =>
    baseapi.get<any>('/ruleengine/catalog/operators', { params: withClientIdParams(params) }),

  getRuleEngineActionsCatalog: (params?: any) =>
    baseapi.get<any>('/ruleengine/catalog/actions', { params: withClientIdParams(params) }),

  getRuleEngineCatalogAssets: (params?: any) =>
    baseapi.get<any>('/ruleengine/catalog/assets', { params: withClientIdParams(params) }),

  fetchDynamicOptions: (apiEndpoint: string, params?: any) =>
    rulebaseapi.get<any>(apiEndpoint, { params: withClientIdParams(params) }),

  getCatalog: (params?: any) =>
    baseapi.get<{ status: boolean; data: CatalogData }>('/automation/catalog', { params: withClientIdParams(params) }),

  getLookups: (params?: any) =>
    baseapi.get<{ status: boolean; data: LookupsData }>('/automation/lookups', { params: withClientIdParams(params) }),

  getTicketingDirectory: (params?: any) =>
    baseapi.get<{ status: boolean; data: any }>('/automation/ticketing_directory', { params: withClientIdParams(params) }),

  getRules: (params?: any) =>
    baseapi.get<any>('/ruleengine/rules', { params: withClientIdParams(params) }),

  getRule: (id: string | number, params?: any) =>
    baseapi.get<any>(`/ruleengine/rules/${id}`, { params: withClientIdParams(params) }),

  createRule: (data: any, params?: any) =>
    baseapi.post<any>('/ruleengine/rules', withClientIdBody(data), { params: withClientIdParams(params) }),

  updateRule: (id: string | number, data: any, params?: any) =>
    baseapi.put<any>(`/ruleengine/rules/${id}`, withClientIdBody(data), { params: withClientIdParams(params) }),

  deleteRule: (id: string | number, params?: any) =>
    baseapi.delete<any>(`/ruleengine/rules/${id}`, { params: withClientIdParams(params) }),

  toggleRule: (id: string | number, data?: any) =>
    baseapi.patch<any>(`/ruleengine/rules/${id}/toggle`, withClientIdBody(data || {}), { params: withClientIdParams() }),

  duplicateRule: (id: string | number) =>
    baseapi.post<any>(`/ruleengine/rules/${id}/duplicate`, withClientIdBody({}), { params: withClientIdParams() }),

  testRule: (data: any) =>
    baseapi.post<any>('/ruleengine/rules/test', withClientIdBody(data), { params: withClientIdParams() }),

  getAlerts: (params?: any) =>
    baseapi.get<any>('/ruleengine/alerts', { params: withClientIdParams(params) }),

  getAlert: (id: string | number,) =>
    baseapi.get<any>(`/ruleengine/alerts/${id}`),

  acknowledgeAlert: (id: string | number,) =>
    baseapi.post<any>(`/ruleengine/alerts/${id}/acknowledge`),

  resolveAlert: (id: string | number,) =>
    baseapi.post<any>(`/ruleengine/alerts/${id}/resolve`),

  approveActionLog: (logId: string | number, data?: any) =>
    baseapi.post<{ status: boolean; message?: string }>(`/automation/action_logs/${logId}/approve`, withClientIdBody(data || {}), { params: withClientIdParams() }),

  getOncall: (params?: any) =>
    baseapi.get<{ status: boolean; data: any[] }>('/automation/oncall', { params: withClientIdParams(params) }),

  createOncall: (data: any) =>
    baseapi.post<{ status: boolean; data: any }>('/automation/oncall', withClientIdBody(data), { params: withClientIdParams() }),

  updateOncall: (id: string | number, data: any) =>
    baseapi.put<{ status: boolean; data: any }>(`/automation/oncall/${id}`, withClientIdBody(data), { params: withClientIdParams() }),

  deleteOncall: (id: string | number) =>
    baseapi.delete<{ status: boolean; message?: string }>(`/automation/oncall/${id}`, { params: withClientIdParams() }),

  getTemplates: (params?: any) =>
    baseapi.get<{ status: boolean; data: any[] }>('/automation/templates', { params: withClientIdParams(params) }),

  installTemplate: (key: string, data?: any) =>
    baseapi.post<{ status: boolean; data: { id: number } }>(`/automation/templates/${key}/install`, withClientIdBody(data || {}), { params: withClientIdParams() }),

  getStats: (params?: any) =>
    baseapi.get<{ status: boolean; data: any }>('/automation/stats', { params: withClientIdParams(params) }),

  getInsights: (params?: any) =>
    baseapi.get<{ status: boolean; data: any }>('/automation/insights', { params: withClientIdParams(params) }),

  getTrends: (params?: any) =>
    baseapi.get<any>('/ruleengine/trends', { params: withClientIdParams(params) }),

  getTrendsSummary: (params?: any) =>
    baseapi.get<any>('/ruleengine/trends/summary', { params: withClientIdParams(params) }),

  getTrendsLeaderboard: (params?: any) =>
    baseapi.get<any>('/ruleengine/trends/leaderboard', { params: withClientIdParams(params) }),
};

